// Shared deterministic noise. Pure functions of (x, z): the same world point always returns the same
// value, so terrain height, the climate/biome fields, and scatter all agree and stream consistently.
// The `seed` lane lets independent fields (temperature vs moisture vs forest density) decorrelate.

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function smoothstep01(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

// Integer hash -> [0,1). seed 0 matches the old unseeded forest-density hash byte-for-byte.
export function hash2(ix: number, iz: number, seed = 0): number {
  let h = (ix * 374761393 + iz * 668265263 + seed * 69069) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// Smooth value noise in [0,1].
export function valueNoise(x: number, z: number, seed = 0): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  const fx = x - xi, fz = z - zi;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const a = hash2(xi, zi, seed), b = hash2(xi + 1, zi, seed);
  const c = hash2(xi, zi + 1, seed), d = hash2(xi + 1, zi + 1, seed);
  return (a + (b - a) * sx) * (1 - sz) + (c + (d - c) * sx) * sz;
}

// Fractal value noise (a few octaves) in ~[0,1].
export function fbm(x: number, z: number, octaves = 4, gain = 0.5, lacunarity = 2, seed = 0): number {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(x * freq, z * freq, seed);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}
